// AuthProvider — bootstraps the auth store on app mount.
//
// Responsibilities:
//   1. On mount, if a token exists in storage, call /auth/me.
//      - 200 → set authenticated
//      - 401 → axios interceptor will try to refresh; if refresh succeeds the
//        retry returns 200; otherwise it dispatches the logout event we
//        subscribe to here.
//   2. Listen for the global 'tricivo:auth-logout' event and clear state.
//   3. Render children unconditionally — pages handle the loading state via
//      ProtectedRoute (which suspends rendering until status !== 'loading').

import { type ReactNode, useEffect } from 'react';
import { onLogoutEvent } from '../api/client';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { storage } from '../utils/storage';
import { useAuthStore } from '../store/authStore';
import type { MeResult } from '../types/api';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setAnonymous = useAuthStore((s) => s.setAnonymous);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      // Tenant tokens live in `accessToken`; super-admin tokens live in
      // `superAccessToken` and are never written to this slot. So a
      // value here always belongs to a tenant (staff/driver) session.
      const token = storage.get('accessToken');
      if (!token) {
        if (!cancelled) setAnonymous();
        return;
      }
      try {
        const res = await apiClient.get<MeResult>(endpoints.auth.me);
        if (cancelled) return;
        setAuthenticated(res.data.user, res.data.url_param_secret);
      } catch {
        if (cancelled) return;
        // The axios interceptor would have already cleared the tenant
        // slots on refresh failure. Mirror that here as belt-and-braces —
        // never touch the super slots from the tenant provider.
        storage.clearTenant();
        setAnonymous();
      }
    }

    void boot();

    const unsubscribe = onLogoutEvent('tenant', () => {
      setAnonymous();
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [setAuthenticated, setAnonymous]);

  return <>{children}</>;
}
